/**
 * Ports the service layer depends on.
 *
 * These exist so services never import one another (rule R2) and never reach for
 * a global (rule R6). The composition root supplies the implementations.
 */

import type { FsDirectory } from '../adapters/fs-port';
import type { Grayscale } from '../domain/sharpness';

/** Epoch milliseconds. Injected so tests are deterministic. */
export type Clock = () => number;

/** Something that wants to know the database changed. DbPersister satisfies it. */
export interface ChangeSink {
  touch(): void;
}

/**
 * Records a decision durably and immediately.
 *
 * Separate from ChangeSink because they answer different questions: the sink is
 * asked "when should the snapshot be written", the journal is told "this must
 * not be lost even if the tab dies in the next millisecond".
 */
export interface DecisionRecorder {
  append(entry: { t: 'bucket'; id: number; v: number | null } | { t: 'rotation'; id: number; v: number }): void;
}

export const noopRecorder: DecisionRecorder = { append: () => undefined };

/**
 * Turns a source id into a readable folder.
 *
 * Owning stored handles is SourceService's job, so every other service receives
 * this function rather than calling SourceService directly.
 */
export type DirectoryResolver = (sourceId: number) => Promise<FsDirectory | null>;

/**
 * Hands back the thumbnail bytes for one photograph, generating them if needed.
 *
 * A port rather than a direct call, because AssistService must not import
 * ThumbnailService (rule R2). The composition root, which is allowed to know
 * about both, wires the two together.
 */
export type ThumbnailBytes = (mediaId: number) => Promise<Uint8Array | null>;

/**
 * Hands back one photograph decoded from its original, large enough to judge
 * focus on. Null when the file cannot be read.
 *
 * A port for the same reason ThumbnailBytes is one: AssistService must not
 * import PreviewService (rule R2), and the composition root is the only place
 * allowed to know about both. Deliberately *not* PreviewService, which caches
 * what it renders — these renders are read once for a number and dropped, and
 * caching three thousand of them would cost hundreds of megabytes with no
 * reader.
 */
export type FocusPixels = (mediaId: number, longEdge: number) => Promise<Grayscale | null>;

export const systemClock: Clock = () => Date.now();

export const noopSink: ChangeSink = { touch: () => undefined };

